import React, { useEffect, useState, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ProjectConfigService from 'service/ClientTransmoraService/ProjectConfigService';
import LoadingSpinner from 'component/LoadingSpinner';
import { ProjectConfigCreateReq } from 'domain/payload/request/clientTransmora/projectConfig/ProjectConfigCreateReq';
import { ProjectConfigUpdateReq } from 'domain/payload/request/clientTransmora/projectConfig/ProjectConfigUpdateReq';
import CustomDropdown from 'component/CustomDropdown';
import ProjectFilterDropdown from 'component/ProjectFilterDropdown';

interface ProjectOption {
  id: string;
  name: string;
}

interface ShowHideSwitches {
  id: boolean;
  name: boolean;
  description: boolean;
  coordinate: boolean;
  createdAt: boolean;
  metricData: boolean;
}

const SHOW_HIDE_FIELDS: { key: keyof ShowHideSwitches; label: string }[] = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'coordinate', label: 'Coordinate' },
  { key: 'createdAt', label: 'Created At' },
  { key: 'metricData', label: 'Metric Data' },
];

const ETL_METHOD_OPTIONS = [{
  id: 'daily',
  name: 'Daily'
}, {
  id: 'realtime',
  name: 'Realtime'
}];

const ProjectConfigForm: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const navigate = useNavigate();
  const projectConfigService = ProjectConfigService();

  // INPUT STATE
  const [projectId, setProjectId] = useState('');
  const [etlMethod, setEtlMethod] = useState('');
  const [switches, setSwitches] = useState<ShowHideSwitches>({
    id: true,
    name: true,
    description: true,
    coordinate: true,
    createdAt: true,
    metricData: true,
  });
  const [keyOptions, setKeyOptions] = useState<{ code: string; name: string }[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const isSearchMounted = useRef(false);
  const skipNextSearchRef = useRef(false);

  // SUPPORTING DATA
  const [projectList, setProjectList] = useState<ProjectOption[]>([]);

  // LOADING
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const fetchKeyConfig = async (
    pId: string,
    search: string = '',
    updateSelectedKeys: boolean = false,
    initialSelectedKeys?: string[]
  ) => {
    if (!pId) {
      setKeyOptions([]);
      if (updateSelectedKeys) setSelectedKeys([]);
      return;
    }
    const token = profile?.data.token;
    if (!token) return;

    setIsLoadingKeys(true);
    try {
      const res = await projectConfigService.ProjectConfigKeyConfig(token, pId, search);
      const keysData = res.data || [];
      const options = keysData.map((k: any) => ({
        code: k.code,
        name: k.name || k.code,
      }));
      setKeyOptions(options);

      if (updateSelectedKeys) {
        if (initialSelectedKeys !== undefined) {
          setSelectedKeys(initialSelectedKeys);
        } else {
          // By default: all selected
          setSelectedKeys(options.map((o: any) => o.code));
        }
      }
    } catch (err) {
      console.error(err);
      setKeyOptions([]);
      if (updateSelectedKeys) setSelectedKeys([]);
    } finally {
      setIsLoadingKeys(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch keys when debounced search term changes
  useEffect(() => {
    if (!isSearchMounted.current) {
      isSearchMounted.current = true;
      return;
    }
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    if (projectId) {
      fetchKeyConfig(projectId, debouncedSearch, false);
    }
  }, [debouncedSearch]);

  const handleProjectChange = (p: any) => {
    const newProjectId = p ? p.id : '';
    setProjectId(newProjectId);
    skipNextSearchRef.current = true;
    setSearchTerm('');
    setDebouncedSearch('');
    if (newProjectId) {
      fetchKeyConfig(newProjectId, '', true);
    } else {
      setKeyOptions([]);
      setSelectedKeys([]);
    }
  };

  const handleToggleSwitch = (field: keyof ShowHideSwitches) => {
    setSwitches((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleToggleKey = (code: string) => {
    setSelectedKeys((prev) =>
      prev.includes(code)
        ? prev.filter((item) => item !== code)
        : [...prev, code]
    );
  };

  const isAllSelected =
    keyOptions.length > 0 &&
    keyOptions.every((item) => selectedKeys.includes(item.code));

  const handleSelectAll = () => {
    if (isAllSelected) {
      const currentCodes = new Set(keyOptions.map((item) => item.code));
      setSelectedKeys((prev) => prev.filter((code) => !currentCodes.has(code)));
    } else {
      const newCodes = keyOptions.map((item) => item.code);
      setSelectedKeys((prev) => Array.from(new Set([...prev, ...newCodes])));
    }
  };

  const buildShowHideKeyString = () => {
    return JSON.stringify({
      id: switches.id,
      name: switches.name,
      description: switches.description,
      coordinate: switches.coordinate,
      createdAt: switches.createdAt,
      metricData: switches.metricData,
      keys: selectedKeys,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingSave(true);
    try {
      if (id === '0') {
        await projectConfigCreate();
        alert('Project Config berhasil dibuat.');
        navigate('/client-project-config');
      } else {
        await projectConfigUpdate();
        alert('Project Config berhasil disimpan.');
        navigate('/client-project-config');
      }
    } catch (err) {
      alert(err);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const projectConfigCreate = async () => {
    const token = profile?.data.token;
    if (!token) return;

    const request: ProjectConfigCreateReq = {
      projectId: projectId,
      etlMethod: etlMethod,
      showHideKey: buildShowHideKeyString(),
    };

    console.log(request);
    await projectConfigService.ProjectConfigCreate(token, request);
  };

  const projectConfigUpdate = async () => {
    const token = profile?.data.token;
    if (!token) return;

    const request: ProjectConfigUpdateReq = {
      id: id!,
      projectId: projectId,
      etlMethod: etlMethod,
      showHideKey: buildShowHideKeyString(),
    };

    console.log(request);
    await projectConfigService.ProjectConfigUpdate(token, request);
  };

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) return;

    setIsLoadingDetail(true);
    try {
      const res = await projectConfigService.ProjectConfigDetail(token, id ?? '0');
      const detail = res.data;
      const loadedProjectId = detail?.projectId ?? '';
      setProjectId(loadedProjectId);
      setEtlMethod(detail?.etlMethod ?? '');

      let parsed: any = null;
      if (typeof detail?.showHideKey === 'string' && detail.showHideKey.trim() !== '') {
        try {
          parsed = JSON.parse(detail.showHideKey);
        } catch (e) {
          parsed = null;
        }
      } else if (typeof detail?.showHideKey === 'object' && detail?.showHideKey !== null) {
        parsed = detail.showHideKey;
      }

      setSwitches({
        id: typeof parsed?.id === 'boolean' ? parsed.id : true,
        name: typeof parsed?.name === 'boolean' ? parsed.name : true,
        description: typeof parsed?.description === 'boolean' ? parsed.description : true,
        coordinate: typeof parsed?.coordinate === 'boolean' ? parsed.coordinate : true,
        createdAt: typeof parsed?.createdAt === 'boolean' ? parsed.createdAt : true,
        metricData: typeof parsed?.metricData === 'boolean' ? parsed.metricData : true,
      });

      const savedKeys: string[] | undefined = Array.isArray(parsed?.keys) ? parsed.keys : undefined;

      if (loadedProjectId) {
        await fetchKeyConfig(loadedProjectId, '', true, savedKeys);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const fetchProjects = async () => {
    const token = profile?.data.token;
    if (!token) return;
    setIsLoadingProjects(true);
    try {
      const excludeId = id !== '0' ? id : undefined;
      const res = await projectConfigService.ProjectConfigConfig(token, excludeId);
      setProjectList(res.data?.projectList ?? []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (id !== '0') {
        await fetchDetail();
      }
      await fetchProjects();
    };
    init();
  }, []);

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">{id === '0' ? 'Add Project Config' : 'Edit Project Config'}</h4>
        <div className="p-4" style={{ background: 'white' }}>
          {isLoadingDetail ? (
            <LoadingSpinner />
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row">

                <div className="col-sm-6">
                  <h6 className="fw-bold mb-3">Basic Information</h6>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Project <span className="text-danger">*</span>
                    </label>
                    <ProjectFilterDropdown
                      projects={projectList as any}
                      defaultSelectedId={projectId || null}
                      onChange={handleProjectChange}
                      placeholder={isLoadingProjects ? 'Memuat project...' : '-- Pilih Project --'}
                      containerClassName="w-100"
                      buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      disabled={isLoadingProjects}
                    />
                  </div>
                </div>

                <div className="col-sm-6">
                  <h6 className="fw-bold mb-3">ETL Proceed</h6>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      ETL Method <span className="text-danger">*</span>
                    </label>
                    <CustomDropdown
                      value={etlMethod}
                      options={ETL_METHOD_OPTIONS}
                      onChange={(opt) => setEtlMethod(opt.id ? String(opt.id) : '')}
                      placeholder="-- Pilih ETL Method --"
                      containerClassName="w-100"
                      buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                    />
                  </div>
                </div>

                <div className="mb-4" />

                <div className="col-sm-12">
                  <h6 className="fw-bold mb-3">Show Hide Key</h6>
                  <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
                    Atur kolom dan dynamic keys yang ditampilkan pada Export Data.
                  </p>

                  {/* Standard Column Switches */}
                  <div className="row mb-4">
                    {SHOW_HIDE_FIELDS.map((field) => (
                      <div className="col-sm-4 col-md-3 col-lg-2 mb-3" key={field.key}>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id={`switch-${field.key}`}
                            checked={switches[field.key]}
                            onChange={() => handleToggleSwitch(field.key)}
                          />
                          <label
                            className="form-check-label fw-semibold"
                            htmlFor={`switch-${field.key}`}
                            style={{ fontSize: '13px' }}
                          >
                            {field.label}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Dynamic Keys Table Selection */}
                  <div className="mt-2">
                    <h6 className="fw-bold mb-3">Dynamic Keys Selection</h6>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="d-flex">
                        <input
                          type="text"
                          className="form-control form-control-compact me-2"
                          placeholder="Search key code / name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          disabled={!projectId}
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => {
                              setSearchTerm('');
                              setDebouncedSearch('');
                            }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="text-muted small">
                        Selected: <span className="fw-semibold text-dark">{selectedKeys.length}</span> / {keyOptions.length} keys
                      </div>
                    </div>

                    <div className="table-responsive border rounded-3" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                      <table className="table align-middle table-hover mb-0">
                        <thead className="table-primary sticky-top bg-white">
                          <tr>
                            <th style={{ width: '35%' }}>Code</th>
                            <th style={{ width: '45%' }}>Name</th>
                            <th className="text-center" style={{ width: '20%' }}>
                              <div className="d-flex align-items-center justify-content-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input mt-0 me-2"
                                  id="selectAllKeys"
                                  checked={isAllSelected}
                                  onChange={handleSelectAll}
                                  disabled={!projectId || isLoadingKeys || keyOptions.length === 0}
                                />
                                <label
                                  htmlFor="selectAllKeys"
                                  className="form-check-label small mb-0"
                                  style={{ cursor: keyOptions.length > 0 ? 'pointer' : 'default' }}
                                >
                                  Checklist
                                </label>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {!projectId ? (
                            <tr>
                              <td colSpan={3} className="text-center py-4 text-muted">
                                Pilih project terlebih dahulu untuk melihat daftar dynamic keys
                              </td>
                            </tr>
                          ) : isLoadingKeys ? (
                            <tr>
                              <td colSpan={3} className="text-center py-4 text-muted">
                                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                Memuat dynamic keys...
                              </td>
                            </tr>
                          ) : keyOptions.length > 0 ? (
                            keyOptions.map((item) => {
                              const isChecked = selectedKeys.includes(item.code);
                              return (
                                <tr
                                  key={item.code}
                                  onClick={() => handleToggleKey(item.code)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td className="fw-semibold">{item.code}</td>
                                  <td>{item.name}</td>
                                  <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={isChecked}
                                      onChange={() => handleToggleKey(item.code)}
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={3} className="text-center py-4 text-muted">
                                {searchTerm
                                  ? 'Tidak ada dynamic key yang cocok dengan pencarian'
                                  : 'Tidak ada dynamic key ditemukan untuk project ini'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="mb-4" />
              </div>

              <div className="w-100 d-flex justify-content-end">
                <Link to="/client-project-config">
                  <button type="button" className="btn btn-outline-primary">
                    Cancel
                  </button>
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary d-flex justify-content-center align-items-center ms-2"
                  disabled={isLoadingSave}
                >
                  {isLoadingSave && (
                    <>
                      <LoadingSpinner reverse={true} />
                      <p className="ms-2"></p>
                    </>
                  )}
                  Save
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectConfigForm;